import React, { useState, useEffect } from 'react';
import { Play, Pause, ChevronRight, CheckCircle, Clock, Star, ArrowLeft, RotateCcw } from 'lucide-react';

const SessionRunner = ({ sessionData, drills, onComplete, onExit }) => {
    // Modes: 'intro', 'active', 'rest', 'summary'
    const [mode, setMode] = useState('intro');
    const [currentDrillIndex, setCurrentDrillIndex] = useState(0);
    const [timer, setTimer] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [rating, setRating] = useState(0);

    const currentDrill = drills[currentDrillIndex];
    const isLastDrill = currentDrillIndex === drills.length - 1;

    // Timer Logic
    useEffect(() => {
        let interval = null;
        if (isActive && mode === 'active') {
            interval = setInterval(() => {
                setTimer(seconds => seconds + 1);
            }, 1000);
        } else if (!isActive && timer !== 0) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isActive, timer, mode]);

    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleStartDrill = () => {
        setMode('active');
        setIsActive(true);
        setTimer(0);
    };

    const handleNextDrill = () => {
        setIsActive(false);
        if (isLastDrill) {
            setMode('summary');
        } else {
            setCurrentDrillIndex(prev => prev + 1);
            setMode('active'); // Or specific 'rest' screen if we wanted
            setTimer(0);
            setIsActive(true); // Auto-start next drill for flow? Or wait? Let's auto-start for now to keep momentum
        }
    };

    const handleFinishSession = () => {
        onComplete({
            rating: rating,
            duration: 45 * 60, // Mock duration or sum of timers
            drillsCompleted: drills.map(d => d.drill_id)
        });
    };

    // --- RENDERERS ---

    if (mode === 'intro') {
        return (
            <div className="h-full bg-gray-900 text-white flex flex-col items-center justify-center p-8 animate-fadeIn">
                <div className="mb-8 p-6 bg-purple-900/20 rounded-full border-2 border-purple-500 animate-pulse">
                    <Play size={48} className="text-purple-400 ml-2" />
                </div>
                <h1 className="text-3xl font-bold mb-2 text-center">{sessionData.day} Session</h1>
                <p className="text-gray-400 mb-8 text-center max-w-xs">{sessionData.focus} Focus • {drills.length} Drills</p>

                <div className="space-y-4 w-full max-w-sm">
                    <div className="bg-gray-800 p-4 rounded-xl flex items-center gap-4">
                        <Clock className="text-blue-400" />
                        <div>
                            <p className="font-bold text-sm">~45 Mins</p>
                            <p className="text-xs text-gray-500">Estimated Time</p>
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleStartDrill}
                    className="mt-12 w-full max-w-sm bg-green-500 text-black font-bold py-4 rounded-xl text-lg hover:bg-green-400 transition transform hover:scale-105"
                >
                    LET'S GO
                </button>
                <button onClick={onExit} className="mt-4 text-gray-500 text-sm hover:text-white">Cancel</button>
            </div>
        );
    }

    if (mode === 'summary') {
        return (
            <div className="h-full bg-gray-900 text-white flex flex-col items-center justify-center p-8 animate-fadeIn">
                <div className="mb-6">
                    <Star size={64} className="text-yellow-400 fill-current animate-bounce" />
                </div>
                <h1 className="text-3xl font-bold mb-2">Session Complete!</h1>
                <p className="text-purple-400 font-bold text-xl mb-8">+150 XP Earned</p>

                <div className="bg-gray-800 p-6 rounded-2xl w-full max-w-sm mb-8">
                    <p className="text-center text-gray-300 mb-4 font-medium">How did that feel?</p>
                    <div className="flex justify-center gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                            <button
                                key={star}
                                onClick={() => setRating(star)}
                                className={`p-2 transition ${rating >= star ? 'text-yellow-400 scale-110' : 'text-gray-600 hover:text-yellow-500/50'}`}
                            >
                                <Star size={32} fill={rating >= star ? "currentColor" : "none"} />
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    onClick={handleFinishSession}
                    disabled={rating === 0}
                    className="w-full max-w-sm bg-white text-black font-bold py-4 rounded-xl text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    FINISH
                </button>
            </div>
        );
    }

    // ACTIVE DRILL MODE
    return (
        <div className="h-full bg-black text-white flex flex-col">
            {/* Header */}
            <div className="p-6 flex justify-between items-center bg-gray-900">
                <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">Drill {currentDrillIndex + 1}/{drills.length}</span>
                </div>
                <button onClick={() => setIsActive(!isActive)} className="bg-gray-800 p-2 rounded-full">
                    {isActive ? <Pause size={20} /> : <Play size={20} />}
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col p-6 overflow-y-auto">
                <h2 className="text-3xl font-bold mb-2">{currentDrill.name}</h2>
                <span className="inline-block bg-purple-900/30 text-purple-400 text-xs font-bold px-3 py-1 rounded-full border border-purple-500/30 w-fit mb-6">
                    {currentDrill.category}
                </span>

                {/* Video Placeholder */}
                <div className="aspect-video bg-gray-800 rounded-2xl border border-gray-700 flex items-center justify-center mb-8 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <Play size={48} className="text-white/20" />
                    <p className="absolute bottom-4 left-4 text-xs text-gray-400 font-mono">VIDEO DEMO LOADING...</p>
                </div>

                {/* Instructions */}
                <div className="mb-8 space-y-4">
                    <div className="flex gap-4">
                        <div className="w-1 bg-purple-500 rounded-full" />
                        <div>
                            <p className="text-gray-400 text-xs uppercase font-bold mb-1">Focus</p>
                            <p className="text-lg font-medium">{currentDrill.target_skill || 'Technique'}</p>
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <div className="w-1 bg-blue-500 rounded-full" />
                        <div>
                            <p className="text-gray-400 text-xs uppercase font-bold mb-1">Instructions</p>
                            <ul className="text-sm text-gray-300 space-y-1">
                                {currentDrill.execution_steps ? (
                                    currentDrill.execution_steps.map((step, i) => <li key={i}>• {step}</li>)
                                ) : (
                                    <li>Follow standard technique.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Controls */}
            <div className="p-6 bg-gray-900 border-t border-gray-800">
                <div className="flex justify-between items-center mb-6">
                    <div className="text-center">
                        <p className="text-4xl font-mono font-bold tabular-nums tracking-wider">{formatTime(timer)}</p>
                        <p className="text-xs text-gray-500 uppercase font-bold mt-1">Duration</p>
                    </div>
                    <div className="text-center">
                        <p className="text-4xl font-mono font-bold tabular-nums text-purple-400">0</p>
                        <p className="text-xs text-gray-500 uppercase font-bold mt-1">Reps</p>
                    </div>
                </div>

                <button
                    onClick={handleNextDrill}
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-4 rounded-xl text-lg shadow-lg shadow-purple-900/20 transition flex items-center justify-center gap-2"
                >
                    {isLastDrill ? 'COMPLETE SESSION' : 'NEXT DRILL'} <ChevronRight size={20} />
                </button>
            </div>
        </div>
    );
};

export default SessionRunner;
